import { TableRowActionsMenu } from '@dealstack/frontend/shared/components/TableRowActionsMenu/TableRowActionsMenu';
import { getMIPSelectedInvestorsData } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import {
	GetParsedInvestorRes,
	GetSelectedInvestorInstanceRes,
} from '@dealstack/shared/lib/generated';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
	TableRowActionGroups,
	TableRowActionOption,
} from '@dealstack/frontend/shared/components/TableRowActionsMenu/TableRowActionsMenu.types.ts';
import { InvestorActionCallbacks, InvestorActionLabels } from './types';

interface Props {
	investor: GetParsedInvestorRes;
	callbacks: InvestorActionCallbacks;
	labels: InvestorActionLabels;
	isLocked?: boolean;
	disableSendInvestorInvitation?: boolean;
	hasEditorRole?: boolean;
	hasExportRole?: boolean;
	hasKycAdminRole?: boolean;
}

export const InvestorActionCell = ({
	investor,
	callbacks,
	labels,
	isLocked,
	disableSendInvestorInvitation,
	hasEditorRole,
	hasExportRole,
	hasKycAdminRole,
}: Props) => {
	const [menuOpened, setMenuOpened] = useState(false);

	// Fetch eligibility data lazily when the menu is opened
	const {
		data: eligibilityData,
		isError: eligibilityRequestFailed,
		isSuccess: eligibilityRequestSucceeded,
		isLoading: eligibilityPending,
	} = useQuery({
		queryKey: [API_PATHS.MIP_INVESTOR_SELECTED_INVESTORS_POST, { selectedIds: [investor.id] }],
		queryFn: ({ signal }) =>
			getMIPSelectedInvestorsData(
				API_PATHS.MIP_INVESTOR_SELECTED_INVESTORS_POST,
				{ selectedIds: [investor.id] },
				signal
			),
		enabled: menuOpened,
		staleTime: 30_000,
	});

	const eligibility: GetSelectedInvestorInstanceRes | undefined =
		eligibilityData?.payload?.investors?.[0];

	const eligibilityUnusable =
		eligibilityRequestFailed || (eligibilityRequestSucceeded && !eligibility);
	const canEdit = !!hasEditorRole && !isLocked;

	const groups: TableRowActionGroups = useMemo(() => {
		const primary: TableRowActionOption[] = [
			{
				label: labels.sendInvite,
				onClick: () => callbacks.onSendInvite(investor.id),
				disabled: !hasEditorRole || isLocked || disableSendInvestorInvitation,
				dataTestId: TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_ROW_ACTION_SEND_INVITE,
			},
			{
				label: labels.sendDocument,
				onClick: () => callbacks.onSendDocument(investor.id),
				disabled: !hasEditorRole || isLocked,
			},
			{
				label: labels.startLeaver,
				onClick: () => callbacks.onStartLeaver(investor.id),
				dataTestId: TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_ROW_ACTION_LEAVER,
				disabled:
					!hasEditorRole ||
					isLocked ||
					(eligibility ? !eligibility.isEligibleForLeavers : true),
				loading: canEdit && eligibilityPending,
			},
			{
				label: labels.startAllocation,
				onClick: () => callbacks.onStartAllocation(investor.id),
				dataTestId: TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_ROW_ACTION_ADD_ALLOCATION,
				disabled:
					!hasEditorRole ||
					isLocked ||
					(eligibility ? !eligibility.isEligibleForAllocations : true),
				loading: canEdit && eligibilityPending,
			},
			...(labels.requestKyc
				? [
						{
							label: labels.requestKyc,
							onClick: () => callbacks.onRequestKyc(investor.id),
							disabled: (!hasEditorRole && !hasKycAdminRole) || isLocked,
						},
					]
				: []),
		];

		const secondary: TableRowActionOption[] = [
			{
				label: labels.download,
				onClick: () => callbacks.onDownload(investor.id),
				disabled: !hasExportRole,
			},
		];

		const tertiary: TableRowActionOption[] = [
			{
				label: labels.removeInvestor,
				onClick: () =>
					callbacks.onRemoveInvestor(
						investor.id,
						investor.person?.displayName ?? labels.removeInvestor
					),
				disabled: isLocked || (eligibility ? !eligibility.isEligibleForRemoval : true),
				loading: !isLocked && eligibilityPending,
			},
		];

		return { primary, secondary, tertiary };
	}, [
		investor.id,
		investor.person?.displayName,
		callbacks,
		labels,
		isLocked,
		disableSendInvestorInvitation,
		eligibility,
		eligibilityPending,
		canEdit,
		hasEditorRole,
		hasExportRole,
		hasKycAdminRole,
	]);

	return (
		<TableRowActionsMenu
			groups={groups}
			onMenuOpen={() => setMenuOpened(true)}
			onMenuClose={() => setMenuOpened(false)}
			notice={eligibilityUnusable ? labels.eligibilityUnavailable : undefined}
			noticeTestId={TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_ROW_ACTION_ELIGIBILITY_NOTICE}
			menuTestId={makeComplexDataTestId(
				TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_TABLE_ACTION_BUTTON,
				investor.person.email
			)}
		/>
	);
};
